import { useEffect, useState } from "react";
import { Autocomplete, TextField } from "@mui/material";
import { useTranslation } from "react-i18next";
import { fetchBrokerNameOptions } from "./brokerNameAutocompleteUtils";

interface BrokerNameFilterAutocompleteProps {
  value?: string;
  onChange: (value: string | undefined) => void;
  sx?: object;
}

export const BrokerNameFilterAutocomplete = ({
  value,
  onChange,
  sx,
}: BrokerNameFilterAutocompleteProps) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState(value ?? "");
  const [options, setOptions] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setInputValue(value ?? "");
  }, [value]);

  useEffect(() => {
    if (!open) return;

    let active = true;
    setLoading(true);

    const timer = setTimeout(async () => {
      try {
        const nextOptions = await fetchBrokerNameOptions(inputValue);
        if (!active) return;
        setOptions(nextOptions);
      } catch {
        if (active) setOptions([]);
      } finally {
        if (active) setLoading(false);
      }
    }, 200);

    return () => {
      active = false;
      clearTimeout(timer);
    };
  }, [inputValue, open]);

  const commit = (next: string) => {
    const trimmed = next.trim();
    onChange(trimmed.length > 0 ? trimmed : undefined);
  };

  return (
    <Autocomplete
      freeSolo
      fullWidth
      size="small"
      clearOnEscape
      open={open}
      openOnFocus
      options={options}
      loading={loading}
      filterOptions={(x) => x}
      autoHighlight
      value={value ?? null}
      inputValue={inputValue}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      onInputChange={(_, newValue, reason) => {
        if (reason === "reset") return;
        setInputValue(newValue);
        if (!newValue.trim()) {
          onChange(undefined);
        }
        if (reason === "input") setOpen(true);
      }}
      onChange={(_, newValue) => {
        const next = typeof newValue === "string" ? newValue : "";
        setInputValue(next);
        commit(next);
      }}
      onBlur={() => {
        commit(inputValue);
        setOpen(false);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          placeholder={(t("allBrokers") as string) ?? ""}
          label={(t("brokerName") as string) ?? ""}
          InputLabelProps={{ shrink: true }}
        />
      )}
      sx={sx}
    />
  );
};
